<template>
    <div class="limiter-content pa-3 pa-lg-0">
        <div class="padding-block"></div>

        <!-- Cabecalho no padrao das telas da area do credor/procurador -->
        <header class="dc-header">
            <span class="dc-eyebrow">
                <v-icon small color="primary">{{ isProcurator ? "mdi-briefcase-account-outline" : "mdi-account-circle-outline" }}</v-icon>
                {{ isProcurator ? "ÁREA DO PROCURADOR" : "ÁREA DO CREDOR" }}
            </span>
            <div class="dc-title-row">
                <h1 class="dc-title">Central <span>de documentos</span></h1>
                <v-btn v-if="user" color="primary" depressed class="dc-btn" @click="addRow">
                    <v-icon left small>mdi-plus</v-icon> Adicionar documentos
                </v-btn>
            </div>
            <p class="dc-subtitle">Documentos que você enviou à plataforma, para usar em habilitações, divergências e credenciamentos.</p>
        </header>

        <template v-if="user">
            <div class="dc-toolbar">
                <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" outlined dense hide-details clearable
                    placeholder="Pesquisar pelo nome ou tipo" class="dc-search"></v-text-field>
                <span class="dc-count">{{ filteredDocuments().length }} {{ filteredDocuments().length === 1 ? "documento" : "documentos" }}</span>
            </div>

            <div v-if="!filteredDocuments().length" class="dc-empty">
                <div class="dc-empty__icon"><v-icon size="34" color="darkSecondary">mdi-folder-open-outline</v-icon></div>
                <div class="dc-empty__title">{{ search ? "Nenhum documento encontrado" : "Nenhum documento enviado" }}</div>
                <div class="dc-empty__text">
                    {{ search ? "Tente pesquisar por outro nome ou tipo." : "Adicione documentos para usar nos seus pedidos à Administração Judicial." }}
                </div>
            </div>

            <article v-for="item in pagedDocuments" :key="item.id" class="dc-row">
                <div class="dc-row__icon"><v-icon small>mdi-file-document-outline</v-icon></div>
                <div class="dc-row__info">
                    <div class="dc-row__name" :title="item.name">{{ item.name }}</div>
                    <div class="dc-muted">{{ item.type || "Tipo não informado" }} · enviado em {{ formatarData(item.created_at) }}</div>
                </div>
                <span class="dc-pill" :class="`dc-pill--${statusOf(item.status).tone}`">
                    <span class="dc-pill__dot"></span>{{ statusOf(item.status).label }}
                </span>
                <div class="dc-row__actions">
                    <v-btn text rounded small color="darkSecondary" class="dc-action" @click="viewDocument(item)">
                        <v-icon left small>mdi-eye-outline</v-icon> Ver
                    </v-btn>
                    <v-btn icon small title="Excluir documento" aria-label="Excluir documento" @click="deleteDocument(item)">
                        <v-icon small color="#b42318">mdi-delete-outline</v-icon>
                    </v-btn>
                </div>
            </article>

            <div v-if="pageCount > 1" class="dc-footer">
                <v-pagination v-model="page" :length="pageCount" :total-visible="7" circle color="primary" />
            </div>
        </template>

        <login-required-notice v-else />

        <v-dialog v-model="modalAdd" max-width="720px">
            <v-card class="dc-dialog">
                <div class="dc-dialog__head">
                    <div>
                        <span class="dc-eyebrow">CENTRAL DE DOCUMENTOS</span>
                        <h2 class="dc-dialog__title">Adicionar documentos</h2>
                    </div>
                    <v-btn icon aria-label="Fechar" @click="closeModalAdd"><v-icon>mdi-close</v-icon></v-btn>
                </div>
                <v-form ref="form" v-model="valid" class="dc-form">
                    <v-card-text class="dc-dialog__body">
                        <p class="dc-muted mb-4">Preencha o nome e o tipo, escolha o arquivo e clique em "Adicionar à lista". Você pode juntar vários e salvar todos de uma vez.</p>
                        <v-text-field outlined dense required :rules="[$rules.required]" v-model="newDocument.name"
                            label="Nome"></v-text-field>

                        <v-select :items="dataset.itemTypes" :rules="[$rules.required]" item-text="name" item-value="id"
                            v-model="newDocument.type" label="Tipo" outlined dense required></v-select>

                        <v-file-input outlined dense required prepend-icon="" prepend-inner-icon="mdi-paperclip" :rules="[$rules.required]"
                            v-model='newDocument.file' label="Arquivo do documento">
                        </v-file-input>

                        <div v-if="pendingDocuments.length > 0" class="dc-pending">
                            <div class="dc-pending__head">Na lista para salvar <span>{{ pendingDocuments.length }}</span></div>
                            <div v-for="(doc, index) in pendingDocuments" :key="index" class="dc-pending__item">
                                <v-icon small color="#7e55ab">mdi-file-document-outline</v-icon>
                                <div class="dc-pending__info">
                                    <strong>{{ doc.name }}</strong>
                                    <span>{{ doc.typeName }}</span>
                                </div>
                                <v-btn icon small aria-label="Tirar da lista" @click="removePendingDocument(index)">
                                    <v-icon small color="#b42318">mdi-close</v-icon>
                                </v-btn>
                            </div>
                        </div>
                    </v-card-text>
                    <v-card-actions class="dc-dialog__footer">
                        <v-btn text class="dc-action" @click="closeModalAdd">Cancelar</v-btn>
                        <v-spacer></v-spacer>
                        <v-btn outlined color="darkSecondary" class="dc-btn" @click="addDocumentToList">
                            <v-icon left small>mdi-playlist-plus</v-icon> Adicionar à lista
                        </v-btn>
                        <v-btn v-if="pendingDocuments.length > 0" depressed color="primary" class="dc-btn" :loading="savingDocuments" @click="saveAllDocuments">
                            Salvar {{ pendingDocuments.length === 1 ? "documento" : `${pendingDocuments.length} documentos` }}
                        </v-btn>
                    </v-card-actions>
                </v-form>
            </v-card>
        </v-dialog>

        <v-dialog v-model="confirmDeleteDialog" persistent max-width="480">
            <v-card class="dc-dialog">
                <div class="dc-dialog__head">
                    <h2 class="dc-dialog__title">Excluir documento</h2>
                </div>
                <v-card-text class="dc-dialog__body">
                    Tem certeza de que deseja excluir <strong>{{ documentToDelete ? documentToDelete.name : "este documento" }}</strong>?
                </v-card-text>
                <v-card-actions class="dc-dialog__footer">
                    <v-spacer></v-spacer>
                    <v-btn text class="dc-action" @click="confirmDeleteDialog = false">Cancelar</v-btn>
                    <v-btn depressed color="error" class="dc-btn" @click="confirmDelete">Excluir</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>



<script>
import { eventHub } from '@/main';

import axios from 'axios';
import itemTypes from "@/common/data/item-types.data.js";

// Documentos societarios (cadastro PJ, "Minha conta"): fora da lista de tipos dos pedidos.
const COMPANY_DOC_LABELS = {
    11: "Contrato/Estatuto social",
    12: "Ata ou documento de eleição do representante",
    13: "Documento do representante legal",
};


export default {
    name: 'DocumentCenterPage',

    data() {
        return {
            user: null,
            valid: true,
            search: '',
            // Paginacao da lista (10 por pagina).
            page: 1,
            perPage: 10,
            savingDocuments: false,
            documents: [],
            modalAdd: false,
            showModal: false,
            newDocument: {},
            pendingDocuments: [],
            dataset: {
                itemTypes: itemTypes,
                loading: false
            },
            confirmDeleteDialog: false,
            documentToDelete: null,
        };
    },

    computed: {
        isProcurator() {
            return !!this.user && Number(this.user.type) === 2;
        },
        pageCount() {
            return Math.ceil(this.filteredDocuments().length / this.perPage);
        },
        pagedDocuments() {
            const start = (this.page - 1) * this.perPage;
            return this.filteredDocuments().slice(start, start + this.perPage);
        },
    },

    watch: {
        search() {
            this.page = 1;
        },
    },

    created() {
        if (this.$root.user) {
            this.user = this.$root.user;
            this.getDocuments();
        }

    },


    methods: {

        filteredDocuments() {
            if (!this.search) {
                return this.documents;
            }
            const searchLower = this.search.toLowerCase();
            return this.documents.filter(doc =>
                doc.name.toLowerCase().includes(searchLower) ||
                doc.type.toLowerCase().includes(searchLower)
            );
        },

        async getDocuments() {
            try {
                const response = await axios.get('/api/user-docs', {
                    params: { start: 1, limit: 100000, search: {} }
                });


                if (response.data && response.data.status && response.data.content) {
                    this.documents = response.data.content.data.map(doc => {

                        const docType = this.dataset.itemTypes.find(type => type.id === doc.type);

                        return {
                            isSelected: false,
                            name: doc.name,
                            type: docType ? docType.name : (COMPANY_DOC_LABELS[Number(doc.type)] || ''),
                            created_at: doc.created_at,
                            file_url: doc.file_url,
                            id: doc.id,
                            status: doc.status
                        };
                    }).sort((a, b) => {
                        return new Date(b.created_at) - new Date(a.created_at);
                    });

                }


            } catch (error) {
                console.error("Erro ao obter documentos", error);
            }
        },

        // Situacao do documento na analise do AJ: 0 em analise, 1 valido, 2 invalido.
        statusOf(status) {
            if (Number(status) === 1) return { label: "Válido", tone: "ok" };
            if (Number(status) === 2) return { label: "Inválido", tone: "danger" };
            return { label: "Em análise", tone: "warn" };
        },

        viewDocument(item) {
            window.open(item.file_url, '_blank');
        },

        addRow() {
            this.modalAdd = true;
        },

        addDocumentToList() {

            const docType = this.dataset.itemTypes.find(type => type.id === this.newDocument.type);

            if (!this.$refs.form.validate()) return;

            this.pendingDocuments.push({
                name: this.newDocument.name,
                type: docType.id,
                typeName: docType.name,
                file: this.newDocument.file
            });

            this.newDocument = { name: '', type: '', file: '' };
            this.$refs.form.reset();
        },

        removePendingDocument(index) {
            this.pendingDocuments.splice(index, 1);
        },

        async saveAllDocuments() {
            this.savingDocuments = true;
            try {
                for (let doc of this.pendingDocuments) {

                    let formData = new FormData();

                    formData.append('name', doc.name);
                    formData.append('type', doc.type);
                    formData.append('file', doc.file);

                    const response = await axios.post('/api/user-docs', formData);

                    // for (let [key, value] of formData.entries()) {
                    //     console.log(key, value);
                    // }

                }

                this.pendingDocuments = [];
                await this.getDocuments();
                this.closeModalAdd();
                this.$dialog.notify.success("Documentos salvos", { position: 'top-right', timeout: 5000 });

            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.savingDocuments = false;
            }
        }
        ,


        // async deleteDocument(item) {
        //     try {

        //         console.log("ID RECEBIDO -----> ", item.id)
        //         await axios.delete(`/api/user-docs/${item.id}`);

        //         // this.documents = this.documents.filter(document => document.id !== item.id);y

        //         this.$dialog.notify.success("Documento deletado com sucesso", { position: 'top-right', timeout: 5000 });
        //     } catch (error) {
        //         console.error("Erro ao deletar documento", error);
        //         this.$dialog.notify.error("Erro ao deletar documento", { position: 'top-right', timeout: 5000 });
        //     }
        // },

        deleteDocument(item) {
            this.documentToDelete = item;
            this.confirmDeleteDialog = true;
        },

        async confirmDelete() {
            if (!this.documentToDelete) return;

            try {
                await axios.delete(`/api/user-docs/${this.documentToDelete.id}`);
                this.documents = this.documents.filter(document => document.id !== this.documentToDelete.id);
                this.$dialog.notify.success("Documento deletado com sucesso", { position: 'top-right', timeout: 5000 });
            } catch (error) {
                console.error("Erro ao deletar documento", error);
                this.$dialog.notify.error("Erro ao deletar documento", { position: 'top-right', timeout: 5000 });
            } finally {
                this.confirmDeleteDialog = false;
                this.documentToDelete = null;
            }
        },

        closeModalAdd() {
            this.newDocument = {
                name: '',
                type: '',
                creationDate: null
            };
            this.modalAdd = false;
        },

        // Carimbo do servidor (UTC) exibido em Brasilia.
        formatarData(dataString) {
            return this.$options.filters.defaultDate(dataString, 'DD/MM/YYYY HH:mm:ss');
        },
    }
}; 
</script>


<style>
.custom-modal-title {
    font-size: 20px;

    @media (max-width: 600px) {
        font-size: 14px;
    }
}
</style>

<style scoped>
/* Mesmo padrao visual de "Meus credores", "Minha conta" e "Resumo de casos". */

.dc-header {
    padding: 32px 0 24px;
}

.dc-eyebrow {
    display: flex;
    gap: 8px;
    align-items: center;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.12em;
    color: #766386;
}

.dc-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    margin: 14px 0 10px;
}

.dc-title {
    margin: 0;
    font-size: clamp(28px, 3.4vw, 40px);
    font-weight: 700;
    color: #25113c;
    letter-spacing: -0.035em;
    line-height: 1.2;
}

.dc-title span {
    color: #806697;
}

.dc-subtitle {
    margin: 0;
    color: #73677c;
    font-size: 14px;
}

.dc-muted {
    font-size: 13px;
    color: rgba(24, 0, 50, 0.55);
}

/* Botoes */
.dc-btn {
    height: 40px !important;
    padding: 0 18px !important;
    border-radius: 10px;
    text-transform: none;
    letter-spacing: 0;
    font-weight: 600;
}

.dc-action {
    text-transform: none;
    letter-spacing: normal;
    font-weight: 600;
}

/* Busca */
.dc-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px 16px;
    flex-wrap: wrap;
    margin-bottom: 14px;
}

.dc-search {
    max-width: 420px;
    width: 100%;
}

.dc-search ::v-deep fieldset,
.dc-form ::v-deep .v-text-field--outlined fieldset {
    border-color: rgba(126, 85, 171, 0.45);
    border-radius: 10px;
}

.dc-search:hover ::v-deep fieldset,
.dc-form ::v-deep .v-text-field--outlined:hover fieldset {
    border-color: #7e55ab;
}

.dc-search ::v-deep .v-input__slot,
.dc-form ::v-deep .v-input__slot {
    border-radius: 10px !important;
}

.dc-count {
    font-size: 12px;
    color: rgba(24, 0, 50, 0.5);
}

/* Linha do documento */
.dc-row {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr) auto auto;
    align-items: center;
    gap: 16px;
    padding: 14px 18px;
    border-radius: 14px;
    background: #fff;
    border: 1px solid rgba(24, 0, 50, 0.07);
    margin-bottom: 8px;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.dc-row:hover {
    border-color: rgba(120, 119, 229, 0.35);
    box-shadow: 0 6px 20px rgba(24, 0, 50, 0.06);
}

.dc-row__icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(120, 119, 229, 0.12);
    color: #7e55ab;
}

.dc-row__icon .v-icon {
    color: inherit;
}

.dc-row__name {
    font-size: 14px;
    font-weight: 600;
    color: #180032;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.dc-row__actions {
    display: flex;
    align-items: center;
    gap: 2px;
}

/* Pilulas de status */
.dc-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
}

.dc-pill__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
}

.dc-pill--ok {
    background: #e7f6ee;
    color: #1e7b4f;
}

.dc-pill--warn {
    background: #fff4dc;
    color: #a16207;
}

.dc-pill--danger {
    background: #fdecea;
    color: #b42318;
}

.dc-footer {
    display: flex;
    justify-content: center;
    margin: 16px 0 32px;
}

/* Estado vazio e visitante */
.dc-empty {
    text-align: center;
    padding: 40px 16px 56px;
}

.dc-empty__icon {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    margin: 0 auto 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(120, 119, 229, 0.12);
}

.dc-empty__title {
    font-size: 17px;
    font-weight: 600;
    color: #180032;
}

.dc-empty__text {
    margin-top: 6px;
    color: rgba(24, 0, 50, 0.6);
    font-size: 14px;
}

/* Dialogos */
.dc-dialog {
    border-radius: 18px !important;
}

.dc-dialog__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    padding: 22px 24px 4px;
}

.dc-dialog__title {
    margin: 6px 0 0;
    font-size: 21px;
    font-weight: 700;
    color: #25113c;
    letter-spacing: -0.02em;
}

.dc-dialog__body {
    padding: 12px 24px 8px !important;
    font-size: 14px;
    color: #2a1a40;
}

.dc-dialog__footer {
    gap: 8px;
    padding: 12px 24px 20px !important;
    flex-wrap: wrap;
}

.dc-pending {
    margin-top: 4px;
    padding: 12px 14px;
    border-radius: 12px;
    background: #faf9fd;
}

.dc-pending__head {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #7e55ab;
    margin-bottom: 6px;
}

.dc-pending__head span {
    margin-left: 4px;
    letter-spacing: 0;
    color: rgba(24, 0, 50, 0.5);
}

.dc-pending__item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 0;
    border-top: 1px solid rgba(24, 0, 50, 0.06);
}

.dc-pending__info {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    font-size: 13px;
}

.dc-pending__info strong {
    color: #180032;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.dc-pending__info span {
    color: rgba(24, 0, 50, 0.55);
}

/* Celular */
@media (max-width: 760px) {
    .dc-row {
        grid-template-columns: auto minmax(0, 1fr);
        gap: 10px 14px;
    }

    .dc-pill,
    .dc-row__actions {
        grid-column: 2;
        justify-self: start;
    }

    .dc-search {
        max-width: none;
    }
}
</style>
